import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  History, Calendar, DollarSign, Clock, TrendingUp, Car, FileText, FileSpreadsheet, Bike,
  Search, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, CalendarDays, Ticket, ReceiptText
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import RefreshButton from '../components/RefreshButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [dailyReport, setDailyReport] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('today');
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [plateFilter, setPlateFilter] = useState('');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('exit_time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [receiptViewer, setReceiptViewer] = useState(null);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200', sort_by: sortBy, sort_order: sortOrder });
      if (plateFilter.trim()) params.set('plate', plateFilter.trim());
      if (vehicleTypeFilter !== 'all') params.set('vehicle_type', vehicleTypeFilter);
      if (serviceTypeFilter !== 'all') params.set('service_type', serviceTypeFilter);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59);
        params.set('date_to', to.toISOString());
      }
      const [historyRes, dailyRes, summaryRes] = await Promise.all([
        axios.get(`${API}/vehicles/history?${params}`, { withCredentials: true }),
        axios.get(`${API}/reports/daily?days=7`, { withCredentials: true }),
        axios.get(`${API}/reports/summary?period=${period}`, { withCredentials: true })
      ]);
      setHistory(historyRes.data);
      setDailyReport(dailyRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [period, plateFilter, vehicleTypeFilter, serviceTypeFilter, dateFrom, dateTo, sortBy, sortOrder]);

  useEffect(() => {
    const timeout = setTimeout(fetchHistory, 300);
    return () => clearTimeout(timeout);
  }, [fetchHistory]);

  const formatCurrency = (amount) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount || 0);
  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-slate-500" />;
    return sortOrder === 'desc' ? <ArrowDown className="w-3 h-3 text-emerald-400" /> : <ArrowUp className="w-3 h-3 text-emerald-400" />;
  };

  const clearFilters = () => {
    setPlateFilter('');
    setVehicleTypeFilter('all');
    setServiceTypeFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('exit_time');
    setSortOrder('desc');
  };

  const hasFilters = plateFilter || vehicleTypeFilter !== 'all' || serviceTypeFilter !== 'all' || dateFrom || dateTo;

  const viewReceipt = async (recordId) => {
    try {
      const { data } = await axios.get(`${API}/vehicles/records/${recordId}/receipt`, { withCredentials: true });
      setReceiptViewer(data.receipt_image);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el comprobante');
    }
  };

  const dayNames = { 'Monday': 'Lunes', 'Tuesday': 'Martes', 'Wednesday': 'Miercoles', 'Thursday': 'Jueves', 'Friday': 'Viernes', 'Saturday': 'Sabado', 'Sunday': 'Domingo' };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4">
              <BackButton to="/dashboard" />
              <div className="border-l border-slate-700 pl-2 sm:pl-4">
                <h1 className="text-base sm:text-lg font-bold text-white">Historial</h1>
                <p className="text-xs text-slate-400 hidden sm:block">Analisis de movimientos</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RefreshButton onClick={fetchHistory} />
              <img src={LOGO_URL} alt="Visual Ruptive" className="h-6 sm:h-8 w-auto hidden sm:block" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        {/* Period + Export */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <h2 className="text-lg sm:text-xl font-bold text-white">Resumen</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 sm:w-40 bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="today">Hoy</SelectItem>
                <SelectItem value="week">Semana</SelectItem>
                <SelectItem value="month">Mes</SelectItem>
                <SelectItem value="all">Todo</SelectItem>
              </SelectContent>
            </Select>
            <a href={`${API}/reports/export/excel`} data-testid="export-excel-btn">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"><FileSpreadsheet className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span></Button>
            </a>
            <a href={`${API}/reports/export/pdf`} target="_blank" rel="noopener noreferrer" data-testid="export-pdf-btn">
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"><FileText className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PDF</span></Button>
            </a>
            <a href={`${API}/reports/export/csv`} target="_blank" rel="noopener noreferrer" data-testid="export-csv-btn">
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-700"><FileSpreadsheet className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">CSV</span></Button>
            </a>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Ingresos</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-400 truncate">{formatCurrency(summary.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-1 mb-0.5"><Car className="w-3 h-3 text-blue-400" /><p className="text-[10px] sm:text-xs text-blue-300 uppercase">Estac.</p></div>
                <p className="text-base sm:text-xl font-bold text-blue-200 truncate">{formatCurrency(summary.parking_revenue || 0)}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">{summary.parking_operations || 0} ops</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-1 mb-0.5"><CalendarDays className="w-3 h-3 text-amber-400" /><p className="text-[10px] sm:text-xs text-amber-300 uppercase">Eventos</p></div>
                <p className="text-base sm:text-xl font-bold text-amber-200 truncate">{formatCurrency(summary.event_revenue || 0)}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">{summary.event_operations || 0} tickets</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Entradas</p>
                <p className="text-lg sm:text-2xl font-bold text-white">{summary.total_entries}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Adentro</p>
                <p className="text-lg sm:text-2xl font-bold text-amber-400">{summary.vehicles_inside}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase">T. Prom.</p>
                <p className="text-lg sm:text-2xl font-bold text-white truncate">{formatDuration(Math.round(summary.average_duration_minutes))}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Daily Chart */}
        <Card className="bg-slate-800 border-slate-700 mb-4 sm:mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Calendar className="w-5 h-5" />Ultimos 7 Dias</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyReport.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No hay datos</p>
            ) : (
              <div className="space-y-3">
                {dailyReport.map((day, i) => {
                  const maxRevenue = Math.max(...dailyReport.map(d => d.revenue), 1);
                  const percentage = (day.revenue / maxRevenue) * 100;
                  return (
                    <div key={day.date} className="flex items-center gap-4">
                      <div className="w-24 text-slate-400 text-sm">{i === 0 ? 'Hoy' : dayNames[day.day_name] || day.day_name}</div>
                      <div className="flex-1 bg-slate-700 rounded-full h-8 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full flex items-center justify-end pr-3"
                          style={{ width: `${Math.max(percentage, 5)}%` }}>
                          {percentage > 30 && <span className="text-white text-sm font-semibold">{formatCurrency(day.revenue)}</span>}
                        </div>
                      </div>
                      {percentage <= 30 && <span className="text-slate-300 text-sm w-24 text-right">{formatCurrency(day.revenue)}</span>}
                      <span className="text-slate-500 text-sm w-16 text-right">{day.entries} veh.</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-slate-800 border-slate-700 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Filtros</span>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-400 hover:text-white ml-auto h-7 px-2" data-testid="clear-filters">
                  <X className="w-3 h-3 mr-1" />Limpiar
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {/* Plate search */}
              <div className="relative col-span-2 md:col-span-2 lg:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={plateFilter} onChange={(e) => setPlateFilter(e.target.value.toUpperCase())}
                  placeholder="Buscar patente..."
                  className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 uppercase font-mono"
                  data-testid="filter-plate" />
              </div>
              {/* Service type */}
              <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="filter-service-type">
                  <SelectValue placeholder="Servicio" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">Todos los servicios</SelectItem>
                  <SelectItem value="parking">Estacionamiento</SelectItem>
                  <SelectItem value="event">Evento</SelectItem>
                </SelectContent>
              </Select>
              {/* Vehicle type */}
              <Select value={vehicleTypeFilter} onValueChange={setVehicleTypeFilter}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="filter-vehicle-type">
                  <SelectValue placeholder="Tipo vehiculo" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                </SelectContent>
              </Select>
              {/* Date from */}
              <div>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white [color-scheme:dark]"
                  data-testid="filter-date-from" />
              </div>
              {/* Date to */}
              <div>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white [color-scheme:dark]"
                  data-testid="filter-date-to" />
              </div>
            </div>
            <div className="flex items-center justify-end mt-3">
              <Badge variant="outline" className="text-slate-300 border-slate-600">
                {history.length} resultados
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* History Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5" />Historial de Movimientos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-slate-400">Cargando...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{hasFilters ? 'No hay resultados con los filtros aplicados' : 'No hay registros aun'}</p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {history.map((record) => (
                    <div key={record.id} className="rounded-lg p-3 bg-slate-700/30 border border-slate-700" data-testid={`history-row-${record.plate}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="font-mono font-bold text-white bg-slate-700 px-2 py-0.5 rounded text-sm">{record.plate}</span>
                          {record.service_type === 'event' && <Badge className="bg-amber-600 text-[10px]"><Ticket className="w-3 h-3 mr-1" />Evento</Badge>}
                          {record.event_addon_name && record.service_type !== 'event' && (
                            <Badge className="bg-amber-600/80 text-[10px]"><Ticket className="w-3 h-3 mr-1" />+Evento</Badge>
                          )}
                          {record.is_member && <Badge className="bg-blue-600 text-[10px]">Socio</Badge>}
                        </div>
                        <span className="text-emerald-400 font-bold text-sm whitespace-nowrap">{record.is_member && record.service_type !== 'event' && !record.event_addon_price ? 'Sin cargo' : formatCurrency(record.amount_charged)}</span>
                      </div>
                      {record.event_name && <p className="text-xs text-amber-300 mb-1 truncate">{record.event_name}</p>}
                      {record.event_addon_name && (
                        <p className="text-xs text-amber-300 mb-1 truncate">+ {record.event_addon_name} ({formatCurrency(record.event_addon_price || 0)})</p>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{formatDate(record.entry_time)} → {record.exit_time ? formatDate(record.exit_time) : 'Adentro'}</span>
                        <div className="flex items-center gap-1">
                          {record.receipt_available && <Button size="sm" variant="ghost" onClick={() => viewReceipt(record.id)} className="h-7 px-2 text-blue-400"><ReceiptText className="h-4 w-4" /></Button>}
                          <Badge variant="outline" className="text-slate-300 border-slate-600 text-[10px]">{formatDuration(record.duration_minutes)}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full" data-testid="history-table">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Patente</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Servicio</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Tipo</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium cursor-pointer select-none hover:text-white transition-colors"
                          onClick={() => toggleSort('entry_time')} data-testid="sort-entry">
                          <span className="flex items-center gap-1">Entrada <SortIcon field="entry_time" /></span>
                        </th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium cursor-pointer select-none hover:text-white transition-colors"
                          onClick={() => toggleSort('exit_time')} data-testid="sort-exit">
                          <span className="flex items-center gap-1">Salida <SortIcon field="exit_time" /></span>
                        </th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium cursor-pointer select-none hover:text-white transition-colors"
                          onClick={() => toggleSort('duration_minutes')} data-testid="sort-duration">
                          <span className="flex items-center gap-1">Duracion <SortIcon field="duration_minutes" /></span>
                        </th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium cursor-pointer select-none hover:text-white transition-colors"
                          onClick={() => toggleSort('amount_charged')} data-testid="sort-amount">
                          <span className="flex items-center gap-1 justify-end">Monto <SortIcon field="amount_charged" /></span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((record) => (
                        <tr key={record.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`history-row-${record.plate}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-white bg-slate-700 px-2 py-1 rounded">{record.plate}</span>
                              {record.is_member && <Badge className="bg-blue-600 text-xs">SOCIO</Badge>}
                              {record.paid_at_entry && record.service_type !== 'event' && <Badge className="bg-amber-600 text-xs">PAGADO</Badge>}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {record.service_type === 'event' ? (
                              <div className="flex flex-col">
                                <Badge className="bg-amber-600 text-xs w-fit"><Ticket className="w-3 h-3 mr-1" />Evento</Badge>
                                {record.event_name && <span className="text-xs text-amber-300 mt-1 truncate max-w-[160px]">{record.event_name}</span>}
                              </div>
                            ) : (
                              <div className="flex flex-col">
                                <Badge variant="outline" className="text-blue-400 border-blue-400/50 text-xs w-fit"><Car className="w-3 h-3 mr-1" />Estac.</Badge>
                                {record.event_addon_name && (
                                  <span className="text-xs text-amber-300 mt-1 truncate max-w-[180px]" title={record.event_addon_name}>
                                    + {record.event_addon_name} ({formatCurrency(record.event_addon_price || 0)})
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className={record.vehicle_type === 'moto' ? 'text-amber-400 border-amber-400/50' : 'text-blue-400 border-blue-400/50'}>
                              {record.vehicle_type === 'moto' ? <Bike className="w-3 h-3 mr-1" /> : <Car className="w-3 h-3 mr-1" />}
                              {record.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-slate-300 text-sm">{formatDate(record.entry_time)}</td>
                          <td className="py-3 px-4 text-slate-300 text-sm">{record.exit_time ? formatDate(record.exit_time) : <Badge variant="outline" className="text-emerald-400 border-emerald-400/50">Adentro</Badge>}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-slate-300 border-slate-600">{formatDuration(record.duration_minutes)}</Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">
                            {record.is_member && record.service_type !== 'event' && !record.event_addon_price ? (
                              <span className="text-blue-400">Sin cargo</span>
                            ) : (
                              <span className="text-emerald-400">{formatCurrency(record.amount_charged)}</span>
                            )}
                            {record.receipt_available && (
                              <Button size="sm" variant="ghost" onClick={() => viewReceipt(record.id)} className="ml-1 h-7 px-2 text-blue-400" aria-label="Ver comprobante">
                                <ReceiptText className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <AppFooter />
      <Dialog open={Boolean(receiptViewer)} onOpenChange={(open) => { if (!open) setReceiptViewer(null); }}>
        <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto border-slate-700 bg-slate-800 text-white">
          <DialogHeader><DialogTitle>Comprobante de pago</DialogTitle></DialogHeader>
          {receiptViewer && <img src={receiptViewer} alt="Comprobante de pago" className="max-h-[75dvh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
